import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { can } from '../../utils/permissions';

const buildCollectionDisplayRows = (items = []) =>
  (Array.isArray(items) ? items : []).map((item, idx) => {
    const excursion =
      typeof item?.excursion === 'string' && item.excursion.trim().length
        ? item.excursion.trim()
        : String(item?.excursion ?? '');
    const passengers = item?.passengers === '' || item?.passengers == null ? '' : Number(item.passengers || 0);
    const totalCollection =
      item?.total_collection === '' || item?.total_collection == null ? '' : Number(item.total_collection || 0);
    return {
      no: String(item?.no ?? idx + 1),
      excursion,
      passengers,
      total_collection: totalCollection,
    };
  });

const sum = (rows = []) => rows.reduce((a, r) => a + Number(r.amount || 0), 0);

const Summary = ({ title, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box width="100%" bgcolor={colors.primary[400]} p={2} borderRadius={2}>
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100] }}>
            € {Number(value || 0).toFixed(2)}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt="2px">
        <Typography variant="h4" sx={{ color: colors.greenAccent[500] }}>
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionList = ({ title, rows = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" color="#4cceac">
        {title}
      </Typography>
      {!rows.length ? (
        <Typography variant="body2">No items.</Typography>
      ) : (
        <Box>
          <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mt={1} mb={1}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
              Name
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
              Amount (€)
            </Typography>
          </Box>
          {rows.map((r, idx) => (
            <Box key={r.id ?? `row-${idx}`} display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
              <Typography variant="body1">{r.name}</Typography>
              <Typography variant="body1">€ {Number(r.amount || 0).toFixed(2)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const DestinationGuideReport = () => {
  const { countryId, guideId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  const handleExportExcel = async () => {
    setExportError('');
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      const query = params.toString();
      const url = `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/destination/${countryId}/${guideId}/export-excel${
        query ? `?${query}` : ''
      }`;
      const res = await fetch(url, { headers: { token } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setExportError(err.message || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const disp = res.headers.get('Content-Disposition');
      const match = disp && disp.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1].trim() : `destination-guide-report_${countryId}_${guideId}.xlsx`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error('Export Excel error', e);
      setExportError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError('');
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        }
        const query = params.toString();
        const url = `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/destination/${countryId}/${guideId}${
          query ? `?${query}` : ''
        }`;
        const res = await fetch(url, { headers: { token } });
        const json = await res.json();
        if (!json.success) {
          setError(json.message || 'Failed to load guide destination report.');
        } else {
          setReport(json.data);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load guide destination report.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId, guideId, startDate, endDate]);

  const backUrl =
    startDate && endDate
      ? `/reports/destinations/${countryId}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
      : `/reports/destinations/${countryId}`;

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Typography color={colors.redAccent[400]}>{error}</Typography>
        <Button
          onClick={() => navigate(backUrl)}
          sx={{
            mt: 2,
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            '&:hover': { backgroundColor: colors.blueAccent[800] },
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box m="20px">
        <Typography>No data available for this guide and destination.</Typography>
        <Button
          onClick={() => navigate(backUrl)}
          sx={{
            mt: 2,
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            '&:hover': { backgroundColor: colors.blueAccent[800] },
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const { guide, summary, collections, sections, guideSummary: guideSummaryFromApi } = report;
  const guideSummary = guideSummaryFromApi ?? {
    totalCollectedAmount: summary?.toBeSettled ?? 0,
    guideAllowanceTotal: 0,
    netSettlementAmount: parseFloat(
      (
        Number(summary?.toBeSettled ?? 0) +
        Number(summary?.totalIncome ?? 0) -
        Number(summary?.excursionExpense ?? 0) +
        Number(summary?.travelExpense ?? 0) -
        Number(summary?.guideExpense ?? 0)
      ).toFixed(2)
    ),
  };
  const resolvedGuideName = [guide?.name, guide?.surname].filter(Boolean).join(' ').trim() || 'Guide';
  const subtitle =
    startDate && endDate
      ? startDate === endDate
        ? `Destination ${countryId} • ${resolvedGuideName} • Departure ${startDate}`
        : `Destination ${countryId} • ${resolvedGuideName} • ${startDate} to ${endDate}`
      : `Destination ${countryId} • ${resolvedGuideName}`;
  const collectionDisplayRows = buildCollectionDisplayRows(collections?.items || []);

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
        <Header title="VIEW GUIDE DESTINATION REPORT" subtitle={subtitle} />
        <Box display="flex" alignItems="center" gap={2}>
          {can('reports.destination.export') && (
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportExcel}
              disabled={exporting}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': { backgroundColor: colors.greenAccent[700] },
              }}
            >
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          )}
          <Button
            onClick={() => navigate(backUrl)}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Go Back
          </Button>
        </Box>
      </Box>
      {exportError && (
        <Typography variant="body2" color={colors.redAccent[500]} sx={{ mb: 2 }}>
          {exportError}
        </Typography>
      )}

      <Box display="flex" flexDirection="column" gap={3}>
        <Box mt={4} p={2} sx={{ background: colors.primary[500], borderRadius: 0, boxShadow: 'none', mb: 1 }}>
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            }}
            gap={2}
          >
            <Summary title="Additional income" value={sum(sections?.income)} />
            <Summary title="Destination-related costs" value={sum(sections?.excursion_expense)} />
            <Summary title="Other income" value={sum(sections?.travel_expense)} />
            <Summary title="Other expenses" value={sum(sections?.guide_expense)} />
          </Box>
        </Box>

        <Divider />

        <Card sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" color="#4cceac" sx={{ mb: 1 }}>
              Destination overview
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, color: colors.grey[100] }}>
              Guide name: {resolvedGuideName}
            </Typography>
            <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1}>
              <Typography variant="body1">Passengers</Typography>
              <Typography variant="body1">{summary?.totalPassengers ?? 0}</Typography>
              <Typography variant="body1">Bookings</Typography>
              <Typography variant="body1">{summary?.totalBookings ?? 0}</Typography>
              <Typography variant="body1">1. Collected amount (€)</Typography>
              <Typography variant="body1">€ {(summary?.totalAmountCollected ?? 0).toFixed(2)}</Typography>
              <Typography variant="body1">2. Guide fee (€)</Typography>
              <Typography variant="body1">€ {(summary?.guideFee ?? 0).toFixed(2)}</Typography>
              <Typography variant="body1">3. Discount (€)</Typography>
              <Typography variant="body1">€ {(summary?.manualDiscountTotal ?? 0).toFixed(2)}</Typography>
              <Typography variant="body1" fontWeight="bold">
                To be settled (€)
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                € {(summary?.toBeSettled ?? 0).toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Divider />

        <Card sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" color="#4cceac" sx={{ mb: 1 }}>
              Revenue by excursion
            </Typography>
            {collections ? (
              <>
                {resolvedGuideName && (
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {resolvedGuideName}
                  </Typography>
                )}
                <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={1} mb={1}>
                  <Box display="grid" gridTemplateColumns="60px 2fr 1fr" gap={1} gridColumn="span 8">
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      No.
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      Excursion
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      Passengers
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color="#4cceac" gridColumn="span 4">
                    Total Collection
                  </Typography>
                </Box>
                {collectionDisplayRows.length > 0 ? (
                  <>
                    {collectionDisplayRows.map((item) => {
                      const hasTotal = item.total_collection !== '' && item.total_collection != null;
                      return (
                        <Box key={item.no} display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={1} mb={0.5}>
                          <Box display="grid" gridTemplateColumns="60px 2fr 1fr" gap={1} gridColumn="span 8">
                            <Typography variant="body1">{item.no}</Typography>
                            <Typography variant="body1">{item.excursion}</Typography>
                            <Typography variant="body1">
                              {item.passengers === '' || item.passengers == null ? '' : item.passengers}
                            </Typography>
                          </Box>
                          <Typography variant="body1" gridColumn="span 4">
                            {hasTotal ? `€ ${Number(item.total_collection).toFixed(2)}` : ''}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Divider sx={{ my: 2 }} />
                    <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="body1">Total amount</Typography>
                      <Typography variant="body1">€ {(collections.totalFullPriceAmount ?? 0).toFixed(2)}</Typography>
                    </Box>
                    <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="body1">Gratis</Typography>
                      <Typography variant="body1">- € {(collections.totalGratisAmount ?? 0).toFixed(2)}</Typography>
                    </Box>
                    <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="body1">Discount</Typography>
                      <Typography variant="body1">- € {(collections.manualDiscountTotal || 0).toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="h6" fontWeight="bold">1. Collected amount</Typography>
                      <Typography variant="h6" fontWeight="bold">€ {(collections.totalAmountCollected ?? 0).toFixed(2)}</Typography>
                    </Box>
                    <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="body1">2. Guide fee ({collections.guideFeePercentage ?? 0}%)</Typography>
                      <Typography variant="body1">- € {(collections.guideFee ?? 0).toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="h6" fontWeight="bold">To be settled (€)</Typography>
                      <Typography variant="h6" fontWeight="bold">
                        € {(collections.toBeSettled ?? summary?.toBeSettled ?? 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body1">No collections data.</Typography>
                )}
              </>
            ) : (
              <Typography variant="body1">No collections data.</Typography>
            )}
          </CardContent>
        </Card>

        <Divider />

        <Card sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" color="#4cceac" sx={{ mb: 1 }}>
              Guide summary
            </Typography>
            <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={2}>
              <Typography variant="body1">Total collected amount</Typography>
              <Typography variant="body1">
                € {Number(guideSummary.totalCollectedAmount ?? 0).toFixed(2)}
              </Typography>
            </Box>

            <SectionList title="Additional income" rows={sections?.income || []} />
            <Divider sx={{ my: 2 }} />
            <SectionList title="Destination-related costs" rows={sections?.excursion_expense || []} />
            <Divider sx={{ my: 2 }} />
            <SectionList title="Other income" rows={sections?.travel_expense || []} />
            <Divider sx={{ my: 2 }} />
            <SectionList title="Other expense" rows={sections?.guide_expense || []} />
            <Divider sx={{ my: 2 }} />

            <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
              <Typography variant="body1">Guide allowance</Typography>
              <Typography variant="body1">
                € {Number(guideSummary.guideAllowanceTotal ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={2}>
              <Typography variant="body1" fontWeight="bold">
                Net settlement amount
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                € {Number(guideSummary.netSettlementAmount ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: 'block', mb: 2 }}>
              Guide allowance is for record only and not included in any totals.
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="#4cceac">
              Mileage
            </Typography>
            <Box mt={1}>
              <Typography variant="body1">
                Total mileage: {summary?.totalNetMileage != null ? Number(summary.totalNetMileage) : '—'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DestinationGuideReport;
